import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, Save, Eye, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface ErstgespraechData {
  // Erstgespraech
  kontaktaufnahme: string;
  anliegen: string;
  erwartungen: string;
  coaching_format: string;
  frequenz: string;
  sitzungsdauer: string;
  erster_eindruck: string;
  // Anamnese
  aktuelle_position: string;
  unternehmen: string;
  branche: string;
  fuehrungsverantwortung: boolean;
  team_groesse: string;
  jahre_position: string;
  beruflicher_werdegang: string;
  anlass: string;
  herausforderungen: string;
  loesungsversuche: string;
  ressourcen_staerken: string;
  lebensumstaende: string;
  gesundheit: string;
  zufriedenheit_beruf: number;
  zufriedenheit_privat: number;
  stresslevel: number;
  energielevel: number;
  selbstwirksamkeit: number;
  motivation_veraenderung: number;
  sonstiges: string;
  // Vereinbarungen & Vertrag
  vereinbarungen: string;
  vertrag_besprochen: boolean;
  vertrag_unterschrieben: boolean;
  // Signatur
  coach_signature?: string;
}

const defaultData: ErstgespraechData = {
  kontaktaufnahme: '',
  anliegen: '',
  erwartungen: '',
  coaching_format: '',
  frequenz: '',
  sitzungsdauer: '',
  erster_eindruck: '',
  aktuelle_position: '',
  unternehmen: '',
  branche: '',
  fuehrungsverantwortung: false,
  team_groesse: '',
  jahre_position: '',
  beruflicher_werdegang: '',
  anlass: '',
  herausforderungen: '',
  loesungsversuche: '',
  ressourcen_staerken: '',
  lebensumstaende: '',
  gesundheit: '',
  zufriedenheit_beruf: 5,
  zufriedenheit_privat: 5,
  stresslevel: 5,
  energielevel: 5,
  selbstwirksamkeit: 5,
  motivation_veraenderung: 5,
  sonstiges: '',
  vereinbarungen: '',
  vertrag_besprochen: false,
  vertrag_unterschrieben: false,
};

interface Props {
  data: any;
  onSave: (data: any) => Promise<void>;
  blockId: string;
  clientEmail: string;
}

export function ErstgespraechForm({ data, onSave, blockId, clientEmail }: Props) {
  const { showToast } = useToast();
  const [form, setForm] = useState<ErstgespraechData>({ ...defaultData, ...data });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    erstgespraech: true,
    anamnese: false,
    vereinbarungen: true,
  });

  // Signature canvas
  const signatureRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Load saved signature on mount
  useEffect(() => {
    const canvas = signatureRef.current;
    if (!canvas || !form.coach_signature) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = form.coach_signature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCanvasPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = signatureRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    const me = e as React.MouseEvent;
    return {
      x: (me.clientX - rect.left) * scaleX,
      y: (me.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getCanvasPos(e);
    lastPos.current = pos;
  }, [getCanvasPos]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  }, [isDrawing, getCanvasPos]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearSignature = useCallback(() => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setForm(prev => ({ ...prev, coach_signature: undefined }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Capture signature canvas before saving
      const canvas = signatureRef.current;
      let signatureData = form.coach_signature;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          let hasContent = false;
          for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] > 0) { hasContent = true; break; }
          }
          signatureData = hasContent ? canvas.toDataURL('image/png') : undefined;
        }
      }
      const saveData = { ...form, coach_signature: signatureData };
      await onSave(saveData);
      setForm(saveData);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/blocks/${blockId}/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Erstgespraech.pdf';
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF heruntergeladen', 'success');
    } catch { showToast('Export fehlgeschlagen', 'error'); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm({ ...form, [name]: (e.target as HTMLInputElement).checked });
    } else if (type === 'range' || type === 'number') {
      setForm({ ...form, [name]: Number(value) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const renderScale = (name: keyof ErstgespraechData, label: string, leftLabel: string, rightLabel: string) => (
    <div className="form-group">
      <label>{label}</label>
      <div className="range-display">
        <input type="range" name={name} min={1} max={10} value={form[name] as number} onChange={handleChange} style={{ flex: 1 }} />
        <span className="range-value">{form[name]}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', paddingTop: 2 }}>
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );

  const SectionHeader = ({ sectionKey, title }: { sectionKey: string; title: string }) => (
    <div
      className="block-form-section-header"
      onClick={() => toggleSection(sectionKey)}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: 'pointer', padding: '10px 0', borderBottom: '1px solid var(--border-light)',
        marginBottom: expandedSections[sectionKey] ? 12 : 0,
      }}
    >
      <h4 style={{ margin: 0 }}>{title}</h4>
      {expandedSections[sectionKey] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </div>
  );

  return (
    <div className="block-form">
      {/* === ERSTGESPRAECH === */}
      <div className="block-form-section">
        <SectionHeader sectionKey="erstgespraech" title="Erstgespräch" />
        {expandedSections.erstgespraech && (
          <>
            <div className="form-group">
              <label>Wie ist der Klient auf Sie aufmerksam geworden?</label>
              <textarea name="kontaktaufnahme" value={form.kontaktaufnahme} onChange={handleChange} placeholder="z.B. Empfehlung, Website, Netzwerk..." />
            </div>
            <div className="form-group">
              <label>Was führt den Klienten zum Coaching?</label>
              <textarea name="anliegen" value={form.anliegen} onChange={handleChange} placeholder="Beschreibung des Anliegens..." />
            </div>
            <div className="form-group">
              <label>Was erwartet der Klient vom Coaching?</label>
              <textarea name="erwartungen" value={form.erwartungen} onChange={handleChange} placeholder="Erwartungen und Wünsche..." />
            </div>
            <div className="form-group">
              <label>Coaching-Format</label>
              <select name="coaching_format" value={form.coaching_format} onChange={handleChange}>
                <option value="">-- Bitte wählen --</option>
                <option value="praesenz">Präsenz</option>
                <option value="online">Online</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Geplante Frequenz</label>
                <select name="frequenz" value={form.frequenz} onChange={handleChange}>
                  <option value="">-- Bitte wählen --</option>
                  <option value="woechentlich">Wöchentlich</option>
                  <option value="14-taegig">14-tägig</option>
                  <option value="monatlich">Monatlich</option>
                  <option value="nach_bedarf">Nach Bedarf</option>
                </select>
              </div>
              <div className="form-group">
                <label>Dauer pro Sitzung</label>
                <select name="sitzungsdauer" value={form.sitzungsdauer} onChange={handleChange}>
                  <option value="">-- Bitte wählen --</option>
                  <option value="60">60 Min</option>
                  <option value="90">90 Min</option>
                  <option value="120">120 Min</option>
                </select>
              </div>
            </div>
            <div className="block-form-coach-only-field">
              <div className="block-form-coach-only-header">
                <label htmlFor="erster_eindruck">Erster Eindruck</label>
                <span className="pm-block-coach-only">
                  <Lock size={10} /> Nur Coach — wird nicht geteilt
                </span>
              </div>
              <textarea
                name="erster_eindruck"
                id="erster_eindruck"
                value={form.erster_eindruck}
                onChange={handleChange}
                placeholder="Beobachtungen, Eindrücke..."
                className="coach-only-textarea"
              />
            </div>
          </>
        )}
      </div>

      {/* === ANAMNESE === */}
      <div className="block-form-section">
        <SectionHeader sectionKey="anamnese" title="Anamnese / Bestandsaufnahme" />
        {expandedSections.anamnese && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Aktuelle Position</label>
                <input type="text" name="aktuelle_position" value={form.aktuelle_position} onChange={handleChange} placeholder="z.B. Teamleiter..." />
              </div>
              <div className="form-group">
                <label>Unternehmen</label>
                <input type="text" name="unternehmen" value={form.unternehmen} onChange={handleChange} placeholder="Name..." />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Branche</label>
                <input type="text" name="branche" value={form.branche} onChange={handleChange} placeholder="z.B. IT, Gesundheitswesen..." />
              </div>
              <div className="form-group">
                <label>Jahre in Position</label>
                <input type="number" name="jahre_position" value={form.jahre_position} onChange={handleChange} min={0} placeholder="0" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: form.fuehrungsverantwortung ? '1fr 1fr' : '1fr', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" name="fuehrungsverantwortung" checked={form.fuehrungsverantwortung} onChange={handleChange} style={{ width: 16, height: 16 }} />
                  Führungsverantwortung
                </label>
              </div>
              {form.fuehrungsverantwortung && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Teamgröße</label>
                  <input type="text" name="team_groesse" value={form.team_groesse} onChange={handleChange} placeholder="Anzahl Mitarbeiter..." />
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Beruflicher Werdegang (Kurzfassung)</label>
              <textarea name="beruflicher_werdegang" value={form.beruflicher_werdegang} onChange={handleChange} placeholder="Wichtigste Stationen..." />
            </div>
            <div className="form-group">
              <label>Anlass des Coachings — Warum jetzt?</label>
              <textarea name="anlass" value={form.anlass} onChange={handleChange} placeholder="Konkreter Anlass..." />
            </div>
            <div className="form-group">
              <label>Aktuelle Herausforderungen</label>
              <textarea name="herausforderungen" value={form.herausforderungen} onChange={handleChange} placeholder="Größte Herausforderungen..." />
            </div>
            <div className="form-group">
              <label>Bisherige Lösungsversuche</label>
              <textarea name="loesungsversuche" value={form.loesungsversuche} onChange={handleChange} placeholder="Was wurde bereits versucht..." />
            </div>
            <div className="form-group">
              <label>Ressourcen & Stärken</label>
              <textarea name="ressourcen_staerken" value={form.ressourcen_staerken} onChange={handleChange} placeholder="Persönliche Stärken, Netzwerk..." />
            </div>
            <div className="form-group">
              <label>Relevante Lebensumstände</label>
              <textarea name="lebensumstaende" value={form.lebensumstaende} onChange={handleChange} placeholder="Familiensituation, soziales Umfeld..." />
            </div>
            <div className="form-group">
              <label>Gesundheit & Wohlbefinden</label>
              <textarea name="gesundheit" value={form.gesundheit} onChange={handleChange} placeholder="Stresssymptome, Schlaf, allgemeines Befinden..." />
            </div>

            <h4 style={{ marginTop: 16, marginBottom: 12 }}>Skalierungsfragen</h4>
            {renderScale('zufriedenheit_beruf', 'Zufriedenheit Beruf', '1 = sehr unzufrieden', '10 = sehr zufrieden')}
            {renderScale('zufriedenheit_privat', 'Zufriedenheit Privatleben', '1 = sehr unzufrieden', '10 = sehr zufrieden')}
            {renderScale('stresslevel', 'Stresslevel', '1 = niedrig', '10 = sehr hoch')}
            {renderScale('energielevel', 'Energielevel', '1 = erschöpft', '10 = voller Energie')}
            {renderScale('selbstwirksamkeit', 'Selbstwirksamkeit', '1 = gering', '10 = sehr hoch')}
            {renderScale('motivation_veraenderung', 'Motivation für Veränderung', '1 = gering', '10 = sehr hoch')}

            <div className="form-group">
              <label>Sonstiges / Weitere Anmerkungen</label>
              <textarea name="sonstiges" value={form.sonstiges} onChange={handleChange} placeholder="Zusätzliche Informationen..." />
            </div>
          </>
        )}
      </div>

      {/* === VEREINBARUNGEN & VERTRAG === */}
      <div className="block-form-section">
        <SectionHeader sectionKey="vereinbarungen" title="Vereinbarungen & Vertrag" />
        {expandedSections.vereinbarungen && (
          <>
            <div className="form-group">
              <label>Was wurde vereinbart?</label>
              <textarea name="vereinbarungen" value={form.vereinbarungen} onChange={handleChange} placeholder="Getroffene Vereinbarungen..." />
            </div>
            <div className="consent-checkboxes">
              <label className="consent-checkbox-item">
                <input type="checkbox" name="vertrag_besprochen" checked={form.vertrag_besprochen} onChange={handleChange} />
                <span>Coaching-Vertrag besprochen</span>
              </label>
              <label className="consent-checkbox-item">
                <input type="checkbox" name="vertrag_unterschrieben" checked={form.vertrag_unterschrieben} onChange={handleChange} />
                <span>Vertrag unterschrieben</span>
              </label>
            </div>

            {/* Coach-Unterschrift */}
            <div className="block-form-signature">
              <label>Coach-Unterschrift</label>
              <canvas
                ref={signatureRef}
                width={700}
                height={150}
                className="signature-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              <div className="signature-actions">
                <button type="button" className="btn btn-sm btn-secondary" onClick={clearSignature}>
                  Löschen
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button className="btn btn-secondary" onClick={handleExport}>
          <Eye size={16} /> PDF-Vorschau
        </button>
        {saved && (
          <span className="save-indicator">
            <Check size={14} /> Gespeichert
          </span>
        )}
      </div>
    </div>
  );
}
